<template>
  <div class="content">
    <!-- Kryteria wyszukiwania -->
    <div class="activity-filters-wrap">
      <UserActivityFilters :loading="loading" @search="search" />
    </div>

    <!-- Przed pierwszym wyszukaniem tabela w ogóle się nie pojawia. -->
    <div v-if="!hasSearched" class="activity-placeholder">
      {{ t('activity-search-hint') }}
    </div>

    <template v-else>
      <div class="activity-summary">
        <span v-if="loading">{{ t('loading') }}</span>
        <span v-else>{{ t('records-found') }}: {{ totalCount }}</span>
      </div>

      <div v-if="!loading && !rows.length" class="activity-placeholder">
        {{ t('no-results') }}
      </div>

      <table v-else class="user-table table table-striped">
        <thead class="thead-light">
          <tr>
            <th style="width: 5%">{{ t('ordinal-number') }}</th>
            <th style="width: 12%">{{ t('username') }}</th>
            <th style="width: 15%">{{ t('localization-name') }}</th>
            <th style="width: 15%">{{ t('logged-since') }}</th>
            <th style="width: 15%">{{ t('logged-to') }}</th>
            <th style="width: 10%">{{ t('duration-time') }}</th>
            <th style="width: 10%">{{ t('ip-address') }}</th>
            <th style="width: 18%">{{ t('type-event') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(item, idx) in rows" :key="getRowKey(item, idx)">
            <tr
              class="activity-row"
              :class="{ 'activity-row-open': isExpanded(item, idx) }"
              @click="toggleDetails(item, idx)"
            >
              <td>{{ (pageNumber - 1) * pageSize + idx + 1 }}</td>
              <td>{{ item.user?.username || "-" }}</td>
              <td>{{ getLocationLabel(item) }}</td>
              <td>{{ formatDate(getStartAt(item)) }}</td>
              <td>{{ formatDate(getEndAt(item)) }}</td>
              <td>{{ getDurationForEvent(item) }}</td>
              <td>{{ item.ip || "-" }}</td>
              <td>{{ getEventLabel(item) }}</td>
            </tr>
            <tr v-if="isExpanded(item, idx)" class="activity-details-row">
              <td colspan="8" class="activity-details-cell">
                <div class="activity-details-grid">
                  <div
                    v-for="(detail, detailIdx) in getEventDetails(item)"
                    :key="detail.label + '-' + detailIdx"
                    class="activity-detail-item"
                  >
                    <span class="activity-detail-label">{{ detail.label }}:</span>
                    <span class="activity-detail-value">{{ detail.value }}</span>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Paginacja po stronie serwera — zmiana strony pobiera kolejną porcję danych. -->
      <PagePanel
        v-if="rows.length"
        :page-num="pageNumber"
        :page-size="pageSize"
        :total-size="totalCount"
        v-on:setPage="setPage"
      />
    </template>
  </div>
</template>

<script>
import moment from "moment";
import Users from "../services/Users";
import PagePanel from "@/components/table/PagePanel";
import UserActivityFilters from "@/components/users/UserActivityFilters";
import { useUserActivityFiltersStore } from "@/stores/userActivityFilters";
import { i18n } from "@/main";
import { userActivityEventKey, userActivityEventLabel } from "@/constants/userActivityEventTypes";

moment.locale("pl"); // Daty formatujemy jawnym wzorcem (DD.MM.YYYY) — locale wpływa tylko na fallbacki.

/**
 * Nazwa filtra zapisanego w metadanych zdarzenia → klucz i18n.
 * Klucze metadanych są nazwami parametrów API, więc muszą zostać po angielsku;
 * tłumaczymy dopiero etykietę pokazywaną w szczegółach.
 */
const FILTER_LABEL_KEYS = {
  performedByUsername: "activity-filter-performed-by",
  plateLpr: "activity-filter-plate",
  vehicleMake: "activity-filter-vehicle-make",
  vehicleModel: "activity-filter-vehicle-model",
  similarity: "activity-filter-similarity",
  overspeed: "activity-filter-overspeed",
  wrongDirection: "activity-filter-wrong-direction",
  onBlackList: "activity-filter-on-black-list",
  overload: "activity-filter-overload",
  overheight: "activity-filter-overheight",
  overlane: "activity-filter-overlane",
  recidivismOnly: "activity-filter-recidivism-only",
  withTags: "activity-filter-with-tags",
  reportType: "activity-filter-report-type",
  dateTimeStart: "activity-filter-date-start",
  dateTimeEnd: "activity-filter-date-end",
  orderDirection: "activity-filter-order-direction",
  orderDirection1: "activity-filter-order-direction",
  orderByColumnName1: "activity-filter-order-by-column",
  withImages: "activity-filter-with-images",
  withAxles: "activity-filter-with-axles",
};

/** Filtry zakresowe (`minXxx` / `maxXxx`) składamy ze wzorca „Min./Max. {wielkość}”. */
const FILTER_RANGE_VALUE_KEYS = {
  Speed: "activity-value-speed",
  TotalLoad: "activity-value-total-load",
  VehLength: "activity-value-veh-length",
  VehHeight: "activity-value-veh-height",
  NumberOfAxles: "activity-value-axles-count",
  OverloadSA: "activity-value-overload-sa",
  OverloadMA: "activity-value-overload-ma",
  OverloadTL: "activity-value-overload-tl",
};

/** Endpoint, z którego pochodzi zapisana operacja → klucz i18n. */
const SOURCE_LABEL_KEYS = {
  "arch-vehicles/list": "activity-source-archive-search",
  "arch-vehicles/report": "activity-source-archive-report",
  "login-to-locations/report": "activity-source-activity-report",
};

/** Format raportu (bez wariantu „ + zdjęcia”, który dokładamy przez i18n). */
const REPORT_FORMAT_LABELS = {
  csv: "CSV",
  xlsx: "XLSX",
  pdf: "PDF",
  xml: "XML",
};

const REPORT_WITH_IMAGE_SUFFIX = "-with-image";

export default {
  name: "UserActivityList",
  components: { PagePanel, UserActivityFilters },
  data() {
    return {
      loading: false,        // Czy trwa pobieranie danych.
      hasSearched: false,    // Czy użytkownik kliknął już „Szukaj” — steruje widocznością tabeli.
      rows: [],              // Bieżąca strona wyników.
      pageNumber: 1,
      pageSize: 10,
      totalCount: 0,
      expandedRowKey: null,
      filtersStore: useUserActivityFiltersStore(),
    };
  },
  methods: {
    /**
     * Obsługa przycisku „Szukaj” — zamraża kryteria i pobiera pierwszą stronę.
     */
    async search() {
      const criteria = this.filtersStore.apply();
      if (!criteria) {
        return; // Formularz niepoprawny — komunikat ustawił store.
      }

      this.pageNumber = 1;
      this.hasSearched = true;
      await this.fetchPage();
    },

    /**
     * Zmiana strony w paginacji. Kryteria pozostają te z ostatniego wyszukiwania.
     */
    async setPage(page) {
      this.pageNumber = page;
      await this.fetchPage();
    },

    /**
     * Pobiera jedną stronę wyników dla zamrożonych kryteriów.
     */
    async fetchPage() {
      const criteria = this.filtersStore.applied;
      if (!criteria) {
        return;
      }

      this.loading = true;
      this.expandedRowKey = null;

      try {
        const response = await Users.getUserActivityList(criteria, this.pageNumber, this.pageSize);
        this.rows = Array.isArray(response?.content) ? response.content : [];
        this.totalCount = Number(response?.totalCount ?? 0);
      } catch (e) {
        console.error(e);
        this.rows = [];
        this.totalCount = 0;
        this.filtersStore.setError(i18n.global.t("activity-fetch-error"));
      } finally {
        this.loading = false;
      }
    },

    getRowKey(item, idx) {
      return `${item.eventType}-${item.id}-${idx}`;
    },
    toggleDetails(item, idx) {
      const key = this.getRowKey(item, idx);
      this.expandedRowKey = this.expandedRowKey === key ? null : key;
    },
    isExpanded(item, idx) {
      return this.expandedRowKey === this.getRowKey(item, idx);
    },
    parseMetadata(metadata) {
      if (metadata == null || metadata === "") {
        return null;
      }
      if (typeof metadata === "object") {
        return metadata;
      }
      if (typeof metadata !== "string") {
        return { value: String(metadata) };
      }
      try {
        return JSON.parse(metadata);
      } catch (e) {
        return { value: metadata };
      }
    },
    formatDetailValue(value) {
      if (value == null || value === "") {
        return "-";
      }
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      return String(value);
    },
    mapFilterLabel(key) {
      const labelKey = FILTER_LABEL_KEYS[key];
      if (labelKey) {
        return this.t(labelKey);
      }

      // minSpeed / maxOverloadTL itd. — wzorzec + nazwa mierzonej wielkości.
      const range = /^(min|max)(.+)$/.exec(key);
      const valueKey = range ? FILTER_RANGE_VALUE_KEYS[range[2]] : null;
      if (valueKey) {
        return this.t(`activity-filter-${range[1]}`, { what: this.t(valueKey) });
      }

      return key;
    },
    mapSourceValue(value) {
      const labelKey = SOURCE_LABEL_KEYS[value];
      return labelKey ? this.t(labelKey) : value;
    },
    mapReportTypeValue(value) {
      const raw = String(value);
      const withImages = raw.endsWith(REPORT_WITH_IMAGE_SUFFIX);
      const format = REPORT_FORMAT_LABELS[
        withImages ? raw.slice(0, -REPORT_WITH_IMAGE_SUFFIX.length) : raw
      ];

      if (!format) {
        return value;
      }

      return withImages ? this.t("report-format-with-images", { format }) : format;
    },
    mapBooleanValue(value) {
      if (value === true || value === "true") {
        return this.t("yes");
      }
      if (value === false || value === "false") {
        return this.t("no");
      }
      return value;
    },
    formatMaybeDateByKey(key, value) {
      if (value == null || value === "") {
        return "-";
      }

      if (key === "dateTimeStart" || key === "dateTimeEnd") {
        return this.formatDate(value) || this.formatDetailValue(value);
      }
      if (key === "source") {
        return this.mapSourceValue(String(value));
      }
      if (key === "reportType") {
        return this.mapReportTypeValue(String(value));
      }
      if (key === "orderDirection" || key === "orderDirection1") {
        const normalized = String(value).toLowerCase();
        if (normalized === "asc") {
          return this.t("sort-asc");
        }
        if (normalized === "desc") {
          return this.t("sort-desc");
        }
      }
      if (key === "eventTypes" && Array.isArray(value)) {
        return value.map((id) => userActivityEventLabel(id)).join(", ");
      }
      if (typeof value === "boolean" || value === "true" || value === "false") {
        return this.formatDetailValue(this.mapBooleanValue(value));
      }

      return this.formatDetailValue(value);
    },
    pushFilterDetail(details, label, key, value) {
      details.push({
        label: this.t("activity-detail-filter", { label: label || this.mapFilterLabel(key) }),
        value: this.formatMaybeDateByKey(key, value),
      });
    },
    getDetailsLocation(evt, metadata) {
      return evt.location?.shortName || metadata?.locationId || "-";
    },
    createBaseDetails(evt, metadata, withSource = false, withLocation = true) {
      const details = [{ label: this.t("type-event"), value: this.getEventLabel(evt) }];

      if (withLocation) {
        details.push({ label: this.t("location"), value: this.getDetailsLocation(evt, metadata) });
      }

      details.push({ label: this.t("ip-address"), value: evt.ip || "-" });
      details.push({
        label: this.t("activity-detail-event-date"),
        value: this.formatDate(this.getStartAt(evt)) || "-",
      });

      if (withSource) {
        details.splice(3, 0, {
          label: this.t("activity-detail-source"),
          value: this.formatMaybeDateByKey("source", metadata?.source ?? "-"),
        });
      }

      return details;
    },
    getEventDetails(evt) {
      const metadata = this.parseMetadata(evt.metadata) || {};
      const eventKey = this.getEventKey(evt);

      if (eventKey === "vms-session") {
        return [
          { label: this.t("type-event"), value: this.getEventLabel(evt) },
          {
            label: this.t("activity-detail-vms-sign"),
            value: evt.vms?.id != null ? `VMS id: ${evt.vms.id}` : "-",
          },
          { label: this.t("ip-address"), value: evt.ip || "-" },
          { label: this.t("activity-detail-vms-on"), value: this.formatDate(this.getStartAt(evt)) || "-" },
          { label: this.t("activity-detail-vms-off"), value: this.formatDate(this.getEndAt(evt)) || "-" },
        ];
      }

      if (eventKey === "archive-search-save") {
        const details = this.createBaseDetails(evt, metadata, true);
        const usedFilters = metadata?.usedFilters && typeof metadata.usedFilters === "object" ? metadata.usedFilters : {};

        this.pushFilterDetail(
          details,
          null,
          "performedByUsername",
          metadata?.performedByUsername || evt.user?.username || "-"
        );
        this.pushFilterDetail(details, null, "dateTimeStart", usedFilters.dateTimeStart ?? metadata?.dateTimeStart ?? "-");
        this.pushFilterDetail(details, null, "dateTimeEnd", usedFilters.dateTimeEnd ?? metadata?.dateTimeEnd ?? "-");
        this.pushFilterDetail(details, null, "orderDirection", usedFilters.orderDirection ?? usedFilters.orderDirection1 ?? "-");
        this.pushFilterDetail(details, null, "orderByColumnName1", usedFilters.orderByColumnName1 ?? "-");
        this.pushFilterDetail(details, null, "withImages", usedFilters.withImages ?? "-");
        this.pushFilterDetail(details, null, "withAxles", usedFilters.withAxles ?? "-");

        return details;
      }

      if (eventKey === "report-download") {
        const details = this.createBaseDetails(evt, metadata, true);

        this.pushFilterDetail(
          details,
          null,
          "performedByUsername",
          metadata?.performedByUsername || evt.user?.username || "-"
        );
        this.pushFilterDetail(details, null, "reportType", metadata?.reportType ?? "-");
        this.pushFilterDetail(details, null, "dateTimeStart", metadata?.dateTimeStart ?? "-");
        this.pushFilterDetail(details, null, "dateTimeEnd", metadata?.dateTimeEnd ?? "-");

        return details;
      }

      if (eventKey === "user-add") {
        const details = this.createBaseDetails(evt, metadata, false, false);
        details.push({
          label: this.t("activity-detail-created-username"),
          value: this.formatDetailValue(metadata?.createdUsername),
        });
        details.push({
          label: this.t("activity-detail-assigned-roles"),
          value: this.formatDetailValue(metadata?.assignedRoles),
        });
        return details;
      }

      if (eventKey === "user-delete") {
        const details = this.createBaseDetails(evt, metadata, false, false);
        details.push({
          label: this.t("activity-detail-deleted-username"),
          value: this.formatDetailValue(metadata?.deletedUsername),
        });
        return details;
      }

      if (eventKey === "permission-grant") {
        const details = this.createBaseDetails(evt, metadata, false, false);
        details.push({
          label: this.t("activity-detail-performed-by-name"),
          value: this.formatDetailValue(metadata?.performedByUsername || evt.user?.username),
        });
        details.push({
          label: this.t("activity-detail-target-user-name"),
          value: this.formatDetailValue(metadata?.targetUsername),
        });
        details.push({
          label: this.t("activity-detail-assigned-roles"),
          value: this.formatDetailValue(metadata?.grantedRoles),
        });
        return details;
      }

      if (eventKey === "permission-revoke") {
        const details = this.createBaseDetails(evt, metadata, false, false);
        details.push({
          label: this.t("activity-detail-performed-by-name"),
          value: this.formatDetailValue(metadata?.performedByUsername || evt.user?.username),
        });
        details.push({
          label: this.t("activity-detail-target-user-name"),
          value: this.formatDetailValue(metadata?.targetUsername),
        });
        details.push({
          label: this.t("activity-detail-revoked-role"),
          value: this.formatDetailValue(metadata?.revokedRoles),
        });
        details.push({
          label: this.t("activity-detail-granted-new-role"),
          value: this.formatDetailValue(metadata?.grantedRoles),
        });
        return details;
      }

      return [
        { label: this.t("type-event"), value: this.getEventLabel(evt) },
        { label: this.t("location"), value: this.getLocationLabel(evt) },
        { label: this.t("ip-address"), value: evt.ip || "-" },
        {
          label: this.t("activity-detail-event-date"),
          value: this.formatDate(this.getStartAt(evt)) || "-",
        },
      ];
    },
    /**
     * Czas trwania sesji w formacie HH:MM:SS (dla zdarzeń chwilowych — pusty).
     */
    getDiff(dateFrom, dateTo) {
      if (!dateFrom || !dateTo) {
        return "";
      }

      const secDiff = moment(dateTo).diff(moment(dateFrom), "seconds");
      if (secDiff < 0) {
        return "";
      }

      const pad = (value) => String(value).padStart(2, "0");
      const hours = Math.floor(secDiff / 3600);
      const minutes = Math.floor((secDiff % 3600) / 60);
      const seconds = secDiff % 60;

      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    },
    /**
     * Klucz logiczny zdarzenia wyliczany z `eventType` zwracanego przez API (1–9).
     */
    getEventKey(evt) {
      return userActivityEventKey(evt?.eventType);
    },
    getEventLabel(evt) {
      return this.getEventKey(evt) ? userActivityEventLabel(evt.eventType) : "-";
    },
    getLocationLabel(evt) {
      const eventKey = this.getEventKey(evt);
      if (eventKey === "login-www") {
        return "SERWER WWW";
      }
      if (eventKey === "vms-session") {
        return evt.vms?.id != null ? `VMS id: ${evt.vms.id}` : "-";
      }
      return evt.location?.shortName ?? "-";
    },
    getStartAt(evt) {
      return evt.loginAt ?? null;
    },
    getEndAt(evt) {
      return evt.logoutAt ?? null;
    },
    getDurationForEvent(evt) {
      return this.getDiff(this.getStartAt(evt), this.getEndAt(evt)) || "-";
    },
    formatDate(value) {
      return value ? moment(value).format("DD.MM.YYYY HH:mm:ss") : "";
    },
  },
};
</script>

<style scoped>
.content {
  width: 100%;
  margin-bottom: 20px;
}

.activity-filters-wrap {
  border-top: 1px solid var(--border-default);
  border-bottom: 1px solid var(--border-default);
  background: var(--surface-primary);
}

/* Komunikat zastępujący tabelę: przed wyszukaniem oraz przy pustym wyniku. */
.activity-placeholder {
  padding: var(--spacing-xl) var(--spacing-lg);
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.activity-summary {
  padding: var(--spacing-sm) var(--spacing-lg);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-subtle);
}

.user-table {
  width: 100%;
  font-size: var(--font-size-sm);
  color: var(--text-primary);
}
.activity-row {
  cursor: pointer;
}
.activity-row.activity-row-open {
  background-color: var(--accent-blue-soft);
}
.activity-details-row {
  background-color: var(--surface-secondary);
}
.activity-details-cell {
  padding: 10px 14px;
}
.activity-details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 6px 14px;
}
.activity-detail-item {
  font-size: var(--font-size-sm);
  line-height: 1.4;
}
.activity-detail-label {
  font-weight: var(--font-weight-medium);
  margin-right: 4px;
}
.activity-detail-value {
  color: var(--text-primary);
}

.pager {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
}
</style>
